export type Category =
  | "All"
  | "Bridge"
  | "DEX"
  | "Derivatives"
  | "Earn"
  | "Gaming"
  | "Infra"
  | "Launchpad"
  | "Lending"
  | "Marketplace"
  | "Boosted APY";

export type AllocationType = "basic" | "deferred" | "cex";

// TODO: Migrate types to monorepo base or shared types package
export type App = {
  id: string;
  icon: string;
  ranking: number | null;
  isXpBoost: boolean;
  isApyBoost: boolean;
  isLive: boolean;
  isVisible: boolean;
  title: string;
  description: string;
  category: Category;
  x?: string | undefined | null;
  website?: string | undefined | null;
  marketingWebsite?: string | undefined | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  // for updated filtering and designs
  quests: Quest[];
  doesCountForPoints: boolean;
  hasExternalPoints: boolean;
  hasRoycoMarket: boolean;
  hasOptimizerVault: boolean;
  isNew: boolean;
};

export type Badge = {
  description: string;
  id: string;
  iconUrl: string;
  isVisible: boolean;
  questId: string;
  title: string;
  earnedAt: string | null;
  quest?: Quest;
};

export type Quest = {
  id: string;
  wave: number;
  badge: Badge;
  title: string;
  description: string;
  numUsersCompleted?: number;
  completedUsers?: User[];
  tasks: Task[];
  xpReward: number;
  liveAt: string;
  boosted: boolean; // boosted if it's related to a top 3 app
  isDoubleRewards: boolean; // added to the quest object to indicate if it's in double rewards time
  doubleRewardsEndTime: Date;
  isUnrevealed: boolean; // added to the quest object to indicate if it's not revealed yet
  isSunset: boolean; // added to the quest object to indicate if it's discontinued
  // may want to replace boosted w/ app relation, unsure yet.
};

export type User = {
  id: string;
  walletAddress: string;
  role: "ADMIN" | "USER";
  referralCode: string;
  referredBy: string | null;
  referralCount: number;
  createdAt: Date;
  updatedAt: Date | null;
  lastLoginAt: Date | null;
  hasCompletedOnboarding: boolean;
  userExperienceMeta: TUserExperienceMeta | undefined;
};

// TODO: extract to root level and combine with hub-api's types.ts
export type TUserExperienceMeta = {
  dismissedBanners?: { [k: string]: boolean };
  bannerExpirations?: { [k: string]: number };
  favorites?: string[];
};

export type Task = {
  id: string;
  header: string;
  title: string;
  description: string;
  completed?: boolean;
  iconUrl: string;
  buttonText: string;
  targetUrl: string;
  relatedApp: App;
  relatedAppId: string;
  position: number;
  createdAt: string;
  updatedAt: string;
};

export type PPSource =
  | "trades"
  | "tvl"
  | "referrals"
  | "staking"
  | "volume"
  | "quests"
  | "daily-spin"
  | "protocol-interactions"
  | "bridging"
  | "sky-society";

export type LeaderboardRecord = {
  walletAddress: string;
  bridgedTotal: number;
  swapVolume: number;
  swapCount: number;
  tvlTotalUsd: number;
  bridgeVolPoints: number;
  swapVolPoints: number;
  protocolsUsed: number;
  referralBonusXp?: number;
  tvlStreakPoints24h: number;
  distinctProtocolPoints: number;
  swapStreakPoints: number;
  longestSwapStreakWeeks: number;
  tvlStreakPoints: number;
  totalXp: number;
  xpRank: number;
  bgRank?: number;
  dateStr: string;
  currentTvlLevels: string;
  longestTvlStreak: number;
  protocol1: string;
  daysUsed1: number;
  protocol2: string;
  daysUsed2: number;
  protocol3: string;
  daysUsed3: number;
  referredBy: string;
  referralCode: string;
  referredByUser?: User;
  plumeStaked: number;
  plumeStakingStreak: number;
  plumeStakingClaimedAmount: {
    plume: number;
    usdc: number;
  };
  TVL: number;
  referrals: number;
  completedQuests: number;
  dailySpinStreak: number;
  plumeStakingLongestStreakDays: number;
  currentPlumeStakingTotalTokens: number;
  referralCount: number;
  plumeStakingPointsEarned: number;
  plumeStakingBonusPointsEarned: number;
};

/* this is the object returned for the top PP box
   with the scores, the delta, and the areas that changed
   the top3PointsDeltasStrings is an array of the top 3 points 
   deltas strings
*/
export type PPScores = {
  activeXp: {
    totalXp: number;
    userSelfXp: number;
    referralBonusXp: number;
    dateStr: string;
  };
  prevXp: {
    totalXp: number;
    dateStr: string;
  };
  top3PointsDeltasStrings: PPSource[];
};

export type AirdropClaim = {
  walletAddress: string;
  initialClaimAmountTokens: number | null;
  initialClaimTimestamp: number | null;
  mainnetClaimAmountTokens: number | null;
  mainnetClaimTimestamp: number | null;
  claimBonusActivated: boolean;
  questBonusActivatedCount: number;
};

export type SeasonOneAllocation = {
  walletAddress: string;
  deferred: boolean;
  cexName: string | null;
  stakingRewards: number;
  claimedBeforeMainnet: number;
  battleGroup: number;
  battleGroupWonAt: string | null;
  calculatedAllocation: number;
  calculatedBoost: number;
  calculatedTotal: number;
  airdropClaim: AirdropClaim | null;
};

export type TokenDetails = {
  token: {
    address: string;
    info: {
      imageThumbUrl: string;
      imageSmallUrl: string;
      imageLargeUrl: string;
    };
    name: string;
    networkId: number;
    symbol: string;
  };
  priceUSD: string;
  change24: string;
  totalSupply?: string;
};

export type TokenDetailsWithBalance = TokenDetails & {
  walletBalance: {
    balance: number;
  };
};

export type SeasonOneStats = {
  flightClass: string;
  miles: number;
  stamps: number;
  referrals: number;
};

export type RoycoTokenPosition = {
  chainId: number;
  contractAddress: string;
  decimals: number;
  fdv: number;
  id: string;
  image: string;
  isUnlocked: boolean;
  issuers: unknown; // TODO Change to specific type if known, e.g. `string[] | null`
  lastUpdated: string; // or `Date` if you plan to parse it
  name: string;
  owner: string | null;
  price: number;
  rawAmount: string;
  rewardStyle: number;
  searchId: string;
  source: string;
  symbol: string;
  tokenAmount: number;
  tokenAmountUsd: number;
  totalSupply: number;
  type: string; // Consider `'token'` if fixed
  unlockTimestamp: string | number; // convert to number if always numeric
  weirollWallet: string;
};

export type TRoycoPosition = {
  lockedInputToken: RoycoTokenPosition;
  unclaimedIncentiveToken: RoycoTokenPosition | undefined;
  claimedIncentiveToken: RoycoTokenPosition | undefined;
};

export type PlumeRaffleUserPrizeData = {
  walletAddress: string;
  prizeId: number;
  ticketsEntered: number;
  isPrizeWinner: boolean;
  didClaimPrize: boolean;
};

export type PlumeDailySpinData = {
  spinHistory: RewardRecord[];
  raffleUserPrizeData: PlumeRaffleUserPrizeData[];
  lastWeekRaffleWinner: {
    txHash: string;
    walletAddress: string;
    rewardCategory: RewardCategory; // You can replace with union of known categories
    rewardAmount: number;
    blockNumber: string;
    streak: number;
    dateStr: string;
    timestamp: number;
  };
  walletContext: {
    address: string;
    isAuthenticatedUser: boolean;
    isAdmin: boolean;
  };
  totalSpinPlumeRewards: number;
  totalSpins: number;
  longestStreak: number;
  totalRaffleTickets: number;
  totalPPEarned: number;
};

export type CampaignTierId =
  | "tier1"
  | "tier2"
  | "tier3"
  | "tier4"
  | "tier5"
  | "bonusTier";
export type CampaignTier = {
  id: CampaignTierId;
  tx?: number;
  pp?: number | null;
  badgeId: string;
};

export type Campaign = {
  startDate: string; // ISO date string
  endDate: string;
  startTx: number;
  targetTx: number;
  tiers: CampaignTier[];
};

export type CampaignData = {
  campaign: Campaign;
  txCount: number;
  totalQualifiedUsers: number;
  top100Users: GuardiansCampaignUserStats[];
};

export type GuardiansCampaignUserStats = {
  walletAddress: string;
  txCount: number;
  rank: number;
};
export enum RewardCategory {
  RaffleTicket = "Raffle Ticket",
  PP = "PP",
  PlumeToken = "Plume Token",
  Nothing = "Nothing",
  Jackpot = "Jackpot",
  NotYetSpun = "Spin to unlock today's reward!",
}
export type RewardRecord = {
  rewardCategory: string;
  rewardAmount: number;
  streak: number;
  dateStr: string;
};
